import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'phosphor-react';
import './RegisterWithBaby.css'; // Reuse same CSS
import { API_BASE_URL } from '../config';

interface RegisterWithInviteProps {
  onRegistrationSuccess?: (user: any) => void;
}

type Step = 'invite' | 'preferences' | 'verify';

const RegisterWithInvite: React.FC<RegisterWithInviteProps> = ({ onRegistrationSuccess }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('invite');

  // Form data
  const [email, setEmail] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [timeFormat24h, setTimeFormat24h] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const getTotalSteps = () => currentStep === 'verify' ? 3 : 2;

  const getCurrentStepNumber = () => {
    switch (currentStep) {
      case 'invite': return 1;
      case 'preferences': return 2;
      case 'verify': return 3;
    }
  };

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setCurrentStep('preferences');
  };

  const handlePreferencesSubmit = async (skip: boolean = false) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register-with-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          invite_code: inviteCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      // Move to verification step
      setCurrentStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          code: verificationCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Verification failed');
      }

      const user = await response.json();

      if (onRegistrationSuccess) {
        onRegistrationSuccess(user);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/request-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to resend code');
      }

      alert('Verification code resent to your email!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setError('');
    switch (currentStep) {
      case 'preferences':
        setCurrentStep('invite');
        break;
      case 'verify':
        setCurrentStep('preferences');
        break;
      case 'invite':
        navigate('/');
        break;
    }
  };

  // Step 1: Email + Invite Code
  if (currentStep === 'invite') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Join with Invite Code</h2>
          <p>Link to an existing baby account</p>

          <form onSubmit={handleInviteSubmit} className="register-form">
            <div className="form-group">
              <label htmlFor="email">Your Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                required
                placeholder="you@example.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="inviteCode">Invite Code</label>
              <input
                type="text"
                id="inviteCode"
                value={inviteCode}
                onChange={(e) => {
                  // Only allow digits and limit to 14 characters
                  const value = e.target.value.replace(/\D/g, '').slice(0, 14);
                  setInviteCode(value);
                  setError('');
                }}
                required
                placeholder="14-digit invite code"
                maxLength={14}
                autoComplete="off"
              />
              <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.875rem' }}>
                Enter the 14-digit code shared with you
              </small>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              disabled={!email || inviteCode.length !== 14}
              className="btn-primary"
            >
              Next
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Step 2: Preferences
  if (currentStep === 'preferences') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Customize your experience</h2>
          <p>You can change these settings later</p>

          <div className="register-form">
            <div className="form-group">
              <label>Preferred Time Format</label>
              <div className="time-format-options">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="timeFormat"
                    checked={!timeFormat24h}
                    onChange={() => setTimeFormat24h(false)}
                  />
                  <span>12-hour (e.g., 2:30 PM)</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="timeFormat"
                    checked={timeFormat24h}
                    onChange={() => setTimeFormat24h(true)}
                  />
                  <span>24-hour (e.g., 14:30)</span>
                </label>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              onClick={() => handlePreferencesSubmit(false)}
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Creating Account...' : 'Create Account & Link'}
            </button>

            <button
              onClick={() => handlePreferencesSubmit(true)}
              disabled={isLoading}
              className="btn-secondary"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Email Verification
  if (currentStep === 'verify') {
    return (
      <div className="register-container">
        <div className="register-card">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} /> Back
          </button>

          <div className="step-indicator">Step {getCurrentStepNumber()} of {getTotalSteps()}</div>
          <h2>Check Your Email</h2>
          <p>We've sent a verification code to {email}</p>

          <form onSubmit={handleVerify} className="register-form">
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value);
                  setError('');
                }}
                required
                placeholder="Enter 10-digit code"
                maxLength={10}
                autoComplete="off"
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              disabled={isLoading || verificationCode.length !== 10}
              className="btn-primary"
            >
              {isLoading ? 'Verifying...' : 'Verify & Start'}
            </button>

            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading}
              className="btn-secondary"
            >
              Resend Code
            </button>
          </form>
        </div>
      </div>
    );
  }

  return null;
};

export default RegisterWithInvite;
